/* eslint-disable react/prop-types */
import * as React from 'react';

import { useBoolean, useMemoizedFn } from 'ahooks';
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Typography,
} from 'antd';

import {
  AntdUpload,
  AntdUploadDragger,
  CheckInputbox,
  DeleteOutlinedIcon,
  DocViewer,
  FileImageOutlinedIcon,
  IntegerInputNumber,
  PictureOutlinedIcon,
  PlaySquareOutlinedIcon,
  PrimitiveList,
  PrimitiveListItem,
  PrimitiveListItemMeta,
  QuestionCircleFilledIcon,
} from 'components';
import { useBrandAction, useBrandValue } from 'context';
import type {
  BrandFileInfo as BrandFileInfoConfig,
  FileOwnerType,
} from 'types';
import { cn, FILE, generateDownloadFile, inputRule, UNIT } from 'utils';

const {
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
} = FILE;
const { SIZE } = UNIT;

const MAX_PICTURE_ITEMS = 4;

type FieldType = Partial<
  Pick<ReturnType<typeof useBrandValue>, 'bannerId'> & Record<string, string>
>;

interface BrandFileInfoProps extends BrandFileInfoConfig {
  ownerType: FileOwnerType;
  maxIndex: number;
  isVideoFile?: boolean;
  handleDeleteFile: () => void;
}

const BrandFileInfo = React.memo<BrandFileInfoProps>(
  ({
    id,
    name,
    title,
    watermark,
    ownerType,
    maxIndex,
    isVideoFile,
    handleDeleteFile,
  }) => (
    <PrimitiveListItem
      className={cn(
        'my-5',
        !isVideoFile && 'mb-0 last:border-solid last:border-inherit',
      )}
    >
      <PrimitiveListItemMeta
        title={
          <Row>
            <Col
              span={24}
              lg={isVideoFile ? 8 : undefined}
              className={cn(isVideoFile && 'lg:pe-3')}
            >
              <Flex
                vertical
                gap='small'
                className={cn(
                  'mb-[--ant-form-item-margin-bottom]',
                  isVideoFile && 'lg:mb-0',
                )}
              >
                <DocViewer
                  documents={[
                    {
                      uri: generateDownloadFile(
                        ownerType,
                        id,
                        IMAGE_SIZE_WIDTH,
                        IMAGE_SIZE_HEIGHT,
                      ),
                      fileName: name,
                    },
                  ]}
                />
                {!isVideoFile ? (
                  <Typography.Text
                    ellipsis={{
                      tooltip: true,
                    }}
                    type='secondary'
                  >
                    <span>تصاویر بزرگ را با سایز</span>
                    <bdi> 778×258 </bdi>
                    <span>و تصاویر کوچک را با سایز</span>
                    <bdi> 512×258 </bdi>
                    <span>پیکسل بارگذاری نمایید.</span>
                  </Typography.Text>
                ) : null}
              </Flex>
            </Col>
            <Col
              span={24}
              lg={isVideoFile ? 16 : undefined}
              className={cn(isVideoFile && 'lg:ps-3')}
            >
              <Row>
                <Col span={24}>
                  <Space.Compact className='w-full'>
                    <Form.Item<FieldType>
                      label='عنوان'
                      name={`file-index-${id}`}
                      className='basis-1/12 [&_label]:text-nowrap'
                    >
                      <IntegerInputNumber
                        placeholder='رتبه'
                        className='border-e-0'
                        max={maxIndex}
                      />
                    </Form.Item>
                    <Form.Item<FieldType>
                      label=' '
                      name={`file-title-${id}`}
                      className='basis-11/12'
                      rules={[inputRule.string]}
                      initialValue={title}
                    >
                      <Input placeholder='عنوان مستند را وارد کنید' />
                    </Form.Item>
                  </Space.Compact>
                </Col>
                {!isVideoFile ? (
                  <Col span={24}>
                    <Form.Item<FieldType>
                      label='آدرس'
                      name={`file-link-${id}`}
                      rules={[inputRule.string]}
                    >
                      <Input.Search
                        placeholder='آدرس مستند را وارد کنید'
                        classNames={{ input: 'text-right [direction:ltr]' }}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col
                  span={24}
                  lg={isVideoFile ? 12 : undefined}
                  className={cn(isVideoFile && 'lg:pe-1')}
                >
                  <Form.Item<FieldType>
                    label='نهان‌نگاری (Watermark)'
                    name={`file-watermark-${id}`}
                    valuePropName='checked'
                    initialValue={watermark}
                  >
                    <CheckInputbox>نیاز به نمایش نهان‌نگاری</CheckInputbox>
                  </Form.Item>
                </Col>
                <Col
                  span={24}
                  lg={isVideoFile ? 12 : undefined}
                  className={cn(isVideoFile && 'lg:ps-1')}
                >
                  <Form.Item<FieldType>
                    className={cn(
                      'mt-[15px]',
                      isVideoFile && 'max-lg:mb-0 lg:mt-[33.5px]',
                      !isVideoFile && 'mb-0',
                    )}
                  >
                    <Popconfirm
                      title='تأیید حذف مستند'
                      description='با حذف این مستند، تمام اطلاعات مرتبط نیز پاک می‌شوند.'
                      okButtonProps={{
                        className: 'bg-[--ant-color-error]',
                      }}
                      icon={
                        <QuestionCircleFilledIcon className='text-[--ant-color-error]' />
                      }
                      onConfirm={handleDeleteFile}
                    >
                      <Button
                        danger
                        type='primary'
                        className='w-full'
                        icon={<DeleteOutlinedIcon />}
                      >
                        حذف مستند
                      </Button>
                    </Popconfirm>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        }
      />
    </PrimitiveListItem>
  ),
);
BrandFileInfo.displayName = 'BrandFileInfo';

const BrandFilesInfo = () => {
  const [picturesLoading, { set: setPicturesLoading }] = useBoolean();
  const [videosLoading, { set: setVideosLoading }] = useBoolean();
  const {
    bannerId,
    bannerStatus,
    pictures = [],
    videos = [],
    ownerType,
  } = useBrandValue();
  const action = useBrandAction();

  const handleDeletePicture = useMemoizedFn((pictureId: string) => {
    const updatedPictures = pictures.filter(
      picture => picture.id !== pictureId,
    );

    action({
      type: 'UPDATE_BRAND_PICTURES',
      payload: {
        pictures: updatedPictures,
      },
    });
  });
  const handleDeleteVideo = useMemoizedFn((videoId: string) => {
    const updatedVideos = videos.filter(document => document.id !== videoId);

    action({
      type: 'UPDATE_BRAND_VIDEOS',
      payload: {
        videos: updatedVideos,
      },
    });
  });

  if (!ownerType) return <React.Fragment />;

  const totalPictures = pictures.length;
  const totalVideos = videos.length;

  return (
    <Row>
      <Col span={24}>
        <Form.Item<FieldType>
          label='برنما (Banner)'
          name='bannerId'
          valuePropName='file'
        >
          <AntdUpload
            className='[&_.ant-upload-list]:h-48'
            listType='picture-card'
            ownerType={ownerType}
            fileList={
              bannerId
                ? [
                    {
                      uid: bannerId,
                      name: 'برنما برند',
                      status: 'done',
                      url: generateDownloadFile(
                        ownerType,
                        bannerId,
                        IMAGE_SIZE_WIDTH,
                        IMAGE_SIZE_HEIGHT,
                      ),
                    },
                  ]
                : undefined
            }
            onChange={({ file: { status, response } }) => {
              action({
                type: 'UPDATE_BRAND_BANNER',
                payload: {
                  bannerId: undefined,
                  bannerStatus: true,
                },
              });

              if (status === 'done' && response) {
                const { id } = response;
                action({
                  type: 'UPDATE_BRAND_BANNER',
                  payload: {
                    bannerId: id,
                    bannerStatus: true,
                  },
                });
              }

              if (status === 'error' || status === 'removed') {
                action({
                  type: 'UPDATE_BRAND_BANNER',
                  payload: {
                    bannerId: undefined,
                    bannerStatus: false,
                  },
                });
              }
            }}
          >
            {!bannerStatus ? (
              <Flex
                vertical
                justify='center'
                align='center'
                gap='middle'
                className='mx-1'
              >
                <FileImageOutlinedIcon className='text-xl text-[--ant-color-link-hover]' />
                <Typography.Paragraph>
                  <span>برنما ترجیحاً با ابعاد</span>
                  <bdi className='mx-1'>
                    {IMAGE_SIZE_HEIGHT}×{320}
                  </bdi>
                  <span>{SIZE} </span>
                  <span>بارگذاری شود.</span>
                </Typography.Paragraph>
              </Flex>
            ) : null}
          </AntdUpload>
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='ویدیو‌های برنما'
          name='videos'
          valuePropName='file'
        >
          <AntdUploadDragger
            withPictures={false}
            withDocuments={false}
            ownerType={ownerType}
            disabled={videosLoading}
            onChange={({ file: { status, response } }) => {
              if (status === 'uploading') {
                setVideosLoading(true);
              }
              if (status === 'done' && response) {
                action({
                  type: 'UPDATE_BRAND_VIDEOS',
                  payload: {
                    videos: [...videos, response],
                  },
                });
              }
              if (status !== 'uploading') {
                setVideosLoading(false);
              }
            }}
          >
            <Flex
              vertical
              justify='center'
              align='center'
              gap='middle'
              className='mx-1'
            >
              <PlaySquareOutlinedIcon className='text-xl text-[--ant-color-link-hover]' />
              <Typography.Paragraph>
                ویدیو‌های برنما را بارگذاری نمایید.
              </Typography.Paragraph>
            </Flex>
          </AntdUploadDragger>
        </Form.Item>
        <PrimitiveList
          loading={{
            spinning: videosLoading,
          }}
          dataSource={videos}
          renderItem={item => {
            const { id } = item;
            return (
              <BrandFileInfo
                isVideoFile
                ownerType={ownerType}
                maxIndex={totalVideos}
                handleDeleteFile={() => {
                  handleDeleteVideo(id);
                }}
                {...item}
              />
            );
          }}
        />
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-4 mt-1' />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='تصاویر ویترین'
          extra={`سقف مجاز بارگذاری تصاویر ویترین، ${MAX_PICTURE_ITEMS} مورد می‌باشد.`}
          name='pictures'
          valuePropName='file'
          className='mb-0'
        >
          <AntdUploadDragger
            withVideos={false}
            withDocuments={false}
            ownerType={ownerType}
            disabled={totalPictures >= MAX_PICTURE_ITEMS || picturesLoading}
            onChange={({ file: { status, response } }) => {
              if (status === 'uploading') {
                setPicturesLoading(true);
              }
              if (status === 'done' && response) {
                action({
                  type: 'UPDATE_BRAND_PICTURES',
                  payload: {
                    pictures: [...pictures, response],
                  },
                });
              }
              if (status !== 'uploading') {
                setPicturesLoading(false);
              }
            }}
          >
            <Flex
              vertical
              justify='center'
              align='center'
              gap='middle'
              className='mx-1'
            >
              <PictureOutlinedIcon className='text-xl text-[--ant-color-link-hover]' />
              <Typography.Paragraph>
                تصاویر ویترین را بارگذاری نمایید.
              </Typography.Paragraph>
            </Flex>
          </AntdUploadDragger>
        </Form.Item>
        <PrimitiveList
          grid={{
            column: 1,
            lg: 2,
            gutter: [20, 0],
          }}
          className='mb-6 lg:*:[&_.ant-spin-container_>_.ant-row]:w-2/5 lg:*:[&_.ant-spin-container_>_.ant-row]:max-w-[40%] lg:[&_.ant-spin-container_>_.ant-row_>_*:is(:nth-child(1),_:nth-child(4))]:w-3/5 lg:[&_.ant-spin-container_>_.ant-row_>_*:is(:nth-child(1),_:nth-child(4))]:max-w-[60%]'
          loading={{
            spinning: picturesLoading,
          }}
          dataSource={pictures}
          renderItem={item => {
            const { id } = item;
            return (
              <BrandFileInfo
                ownerType={ownerType}
                maxIndex={totalPictures}
                handleDeleteFile={() => {
                  handleDeletePicture(id);
                }}
                {...item}
              />
            );
          }}
        />
      </Col>
    </Row>
  );
};

export { BrandFileInfo, BrandFilesInfo };
